import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

// Define the Tab interface
interface Tab {
  id: string;
  label: string;
}

// Define the AnimatedTabs props
interface AnimatedTabsProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  className?: string;
}

// AnimatedTabs component
export const AnimatedTabs: React.FC<AnimatedTabsProps> = ({
  tabs,
  activeTab,
  onTabChange,
  className
}) => {
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const tabsRef = useRef<(HTMLButtonElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  // Update indicator position and center active tab
  useEffect(() => {
    const activeIndex = tabs.findIndex(tab => tab.id === activeTab);
    const activeTabElement = tabsRef.current[activeIndex];
    
    if (activeTabElement && containerRef.current) {
      const { offsetLeft, offsetWidth } = activeTabElement;
      const containerWidth = containerRef.current.offsetWidth;
      
      // Update indicator position
      setIndicatorStyle({
        left: offsetLeft,
        width: offsetWidth
      });

      // Center active tab
      const tabCenter = offsetLeft + offsetWidth / 2;
      const containerCenter = containerWidth / 2;
      const newScrollLeft = tabCenter - containerCenter;

      containerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  }, [activeTab, tabs]);

  return (
    <div className={cn("relative w-full max-w-full mx-auto bg-gradient-to-b from-teal-50 to-white rounded-xl p-1 shadow-md", className)}>
      <div ref={containerRef} className="relative flex items-center justify-center overflow-x-auto scrollbar-hide rounded-lg bg-white/90">
        {/* Glossy overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-transparent to-teal-100/10 rounded-lg pointer-events-none" />

        {/* Animated glow indicator for active tab */}
        <motion.div
          className="absolute top-0 h-full rounded-lg z-10"
          style={{
            background: 'radial-gradient(circle, rgba(6, 182, 212, 0.3) 0%, transparent 70%)',
          }}
          animate={indicatorStyle}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 30,
          }}
        />

        {/* Tab buttons */}
        <div className="flex items-center justify-center gap-4 p-2 sm:p-2 px-4 sm:px-2">
          {tabs.map((tab, index) => (
            <motion.button
              key={tab.id}
              ref={el => tabsRef.current[index] = el}
              onClick={() => onTabChange(tab.id)}
              className={cn(
                "relative z-20 px-6 py-2 text-lg font-bold rounded-full transition-all duration-200 sm:px-6 px-4 sm:py-2 py-3",
                activeTab === tab.id
                  ? "text-white bg-teal-700 shadow-lg"
                  : "text-teal-800 bg-white/80 hover:bg-gray-100"
              )}
              whileTap={{ scale: 0.96 }}
            >
              <span className="whitespace-nowrap">{tab.label}</span>
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
};